import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTrial } from '../context/TrialContext';
import { useAuth } from '../context/AuthContext';
import {
  fetchPlans,
  fetchSubscriptions,
  changePlan,
  createPaymentLink,
  formatPrice,
} from '../utils/astropayService';
import toast from 'react-hot-toast';
import {
  ArrowLeft,
  CheckCircle2,
  Crown,
  Star,
  CreditCard,
  ArrowRight,
  Calendar,
  Clock,
  Zap,
  Shield,
  TrendingUp,
  RefreshCcw,
  ChevronRight,
  AlertTriangle,
  ExternalLink,
  XCircle,
} from 'lucide-react';
import { supabase } from '../supabase';
import PlanUsagePanel from '../components/PlanUsagePanel';

export default function GestionPlan() {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const {
    currentPlan,
    currentPlanKey,
    isTrialing,
    isActive,
    isGrace,
    isExpired,
    isReadOnly,
    daysLeft,
    graceDaysLeft,
    subscriptionExpiresAt,
    organization,
    latestSubscription,
    refreshTrial,
    billingProviderLabel,
  } = useTrial();

  const [plans, setPlans] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  const autoRenew = organization?.auto_renew !== false; // default true

  const orgId = profile?.organization_id;

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [plansData, historyData] = await Promise.all([
        fetchPlans(),
        orgId ? fetchSubscriptions(orgId) : [],
      ]);
      setPlans(plansData);
      setHistory(historyData);
    } catch (err) {
      console.error('[GestionPlan] loadData error:', err);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSelectPlan = async (plan) => {
    if (plan.key === currentPlanKey && isActive) {
      toast('Ya tenés este plan activo.', { icon: '✅' });
      return;
    }

    setSelectedPlan(plan);
  };

  const handleConfirmPlan = async () => {
    if (!selectedPlan || !orgId) return;
    setProcessing(true);

    const toastId = toast.loading('Procesando cambio de plan...');

    try {
      const result = await changePlan({
        orgId,
        newPlanKey: selectedPlan.key,
      });

      await refreshTrial();
      await loadData();

      if (result.payment_url) {
        toast.success('Link de pago generado. Redirigiendo...', { id: toastId });
        setTimeout(() => window.open(result.payment_url, '_blank'), 1000);
      } else {
        toast.success(
          `Plan cambiado a ${selectedPlan.name}. El pago se procesara cuando ${billingProviderLabel} este configurado.`,
          { id: toastId, duration: 5000 }
        );
      }

      setSelectedPlan(null);
    } catch (err) {
      console.error(err);
      toast.error(err?.message || 'Error al cambiar plan', { id: toastId });
    } finally {
      setProcessing(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!orgId) return;
    setCancelling(true);
    try {
      const { error } = await supabase.rpc('cancel_subscription', {
        p_org_id: orgId,
        p_reason: cancelReason?.trim() || null,
      });
      if (error) throw error;
      await refreshTrial();
      toast.success('Suscripción cancelada. Seguís usando Gastify hasta el vencimiento del período actual.', {
        duration: 6000,
      });
      setShowCancelModal(false);
      setCancelReason('');
    } catch (err) {
      console.error(err);
      toast.error(err?.message || 'No se pudo cancelar la suscripción.');
    } finally {
      setCancelling(false);
    }
  };

  const handleReactivateAutoRenew = async () => {
    if (!orgId) return;
    setCancelling(true);
    try {
      const { error } = await supabase.rpc('reactivate_auto_renew', { p_org_id: orgId });
      if (error) throw error;
      await refreshTrial();
      toast.success('Renovación automática reactivada.');
    } catch (err) {
      toast.error(err?.message || 'No se pudo reactivar la renovación.');
    } finally {
      setCancelling(false);
    }
  };

  const handleRenew = async () => {
    if (!orgId || !currentPlan) return;
    setProcessing(true);
    const toastId = toast.loading('Generando link de pago...');

    try {
      const result = await createPaymentLink({
        orgId,
        planKey: currentPlan.key || currentPlanKey,
        planName: currentPlan.name,
        amount: currentPlan.price_monthly,
        currency: currentPlan.currency || 'ARS',
      });

      await refreshTrial();
      await loadData();

      if (result.payment_url) {
        toast.success('Link de pago generado', { id: toastId });
        window.open(result.payment_url, '_blank');
      } else {
        toast.success('Suscripción registrada. Pendiente de configuración de pago.', {
          id: toastId,
          duration: 5000,
        });
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.message || 'Error al renovar', { id: toastId });
    } finally {
      setProcessing(false);
    }
  };

  // ── Plan card badge helper ─────────────────────────────────────
  const getPlanBadge = (plan, index) => {
    if (plan.key === currentPlanKey) return { label: 'Tu plan actual', color: 'bg-[var(--color-accent-dark)]' };
    if (index === 1) return { label: 'Más elegido', color: 'bg-[#3F3F46]' };
    if (index === 2) return { label: 'Más completo', color: 'bg-[#10B981]' };
    return null;
  };

  const planIcons = {
    basic: <Shield size={20} className="text-blue-500" />,
    premium: <Star size={20} className="text-purple-500" />,
    enterprise: <Crown size={20} className="text-[var(--color-accent-dark)]" />,
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FBF8F1] flex items-center justify-center p-4">
        <div className="w-10 h-10 border-2 border-[#E4E4E7] border-t-[#10B981] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FBF8F1] py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-[#A1A1AA] hover:text-[#3F3F46] text-[12px] font-semibold mb-4 transition-all"
          >
            <ArrowLeft size={14} /> Volver al sistema
          </button>

          <div className="flex items-center gap-3 mb-2">
            <div className="h-12 w-12 rounded-md bg-[#10B981] flex items-center justify-center shadow-lg">
              <span className="text-white font-black text-[20px]">G</span>
            </div>
            <div>
              <h1 className="text-[22px] font-black text-[#3F3F46]">Gestión de Plan</h1>
              <p className="text-[12px] text-[#A1A1AA]">
                Elegí el plan que mejor se adapte a tu negocio
              </p>
            </div>
          </div>
        </div>

        {/* Banner de suscripción cancelada (auto_renew off) */}
        {!autoRenew && organization?.cancellation_at && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex flex-wrap items-start gap-3">
            <XCircle size={20} className="text-amber-600 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-[200px]">
              <p className="text-[13px] font-bold text-amber-900">
                Cancelaste la renovación automática
              </p>
              <p className="text-[12px] text-amber-800 mt-1">
                Vas a poder usar Gastify hasta el {subscriptionExpiresAt
                  ? subscriptionExpiresAt.toLocaleDateString('es-AR')
                  : 'fin del período actual'}. Después la cuenta pasa a modo solo lectura.
              </p>
            </div>
            <button
              onClick={handleReactivateAutoRenew}
              disabled={cancelling}
              className="px-4 py-2 rounded-lg bg-amber-600 text-white text-[11px] font-black uppercase tracking-widest hover:bg-amber-700 transition-all shadow-sm disabled:opacity-50"
            >
              Reactivar renovación
            </button>
          </div>
        )}

        {/* Panel de uso del ciclo actual */}
        <PlanUsagePanel className="mb-6" />

        {/* Status card */}
        <div className="bg-[var(--surface)] rounded-xl border border-[#E4E4E7] p-5 mb-6 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-[#D1FAE5] flex items-center justify-center">
                {planIcons[currentPlanKey] || <Zap size={20} className="text-[var(--color-accent-dark)]" />}
              </div>
              <div>
                <p className="text-[10px] font-black uppercase tracking-widest text-[#A1A1AA]">
                  Plan actual
                </p>
                <p className="text-[16px] font-black text-[#3F3F46]">
                  {currentPlan?.name || currentPlanKey || 'Sin plan'}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-6">
              {/* Status badge */}
              <div className="text-center">
                <p className="text-[9px] font-black uppercase tracking-widest text-[#A1A1AA] mb-1">Estado</p>
                <span
                  className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${
                    isActive
                      ? 'bg-[var(--color-accent-soft)] text-[var(--color-accent-dark)] border border-[color:var(--color-accent)]'
                      : isTrialing
                      ? 'bg-blue-50 text-blue-700 border border-blue-200'
                      : isGrace
                      ? 'bg-orange-50 text-orange-700 border border-orange-200'
                      : 'bg-red-50 text-red-700 border border-red-200'
                  }`}
                >
                  <span
                    className={`w-1.5 h-1.5 rounded-full ${
                      isActive ? 'bg-[var(--color-accent-dark)]' : isTrialing ? 'bg-blue-500' : isGrace ? 'bg-orange-500' : 'bg-red-500'
                    }`}
                  />
                  {isActive
                    ? 'Activa'
                    : isTrialing
                    ? `Trial (${daysLeft}d)`
                    : isGrace
                    ? `Gracia (${graceDaysLeft}d)`
                    : 'Vencida'}
                </span>
              </div>

              {/* Price */}
              <div className="text-center">
                <p className="text-[9px] font-black uppercase tracking-widest text-[#A1A1AA] mb-1">Precio</p>
                <p className="text-[16px] font-black text-[#3F3F46]">
                  {formatPrice(currentPlan?.price_monthly || 0)}
                  <span className="text-[10px] font-bold text-[#A1A1AA]">/mes</span>
                </p>
              </div>

              {/* Expiration */}
              {subscriptionExpiresAt && (
                <div className="text-center">
                  <p className="text-[9px] font-black uppercase tracking-widest text-[#A1A1AA] mb-1">Vence</p>
                  <p className="text-[13px] font-bold text-[#3F3F46]">
                    {subscriptionExpiresAt.toLocaleDateString('es-AR')}
                  </p>
                </div>
              )}
            </div>

            {/* Activar / Renovar */}
            {(isTrialing || isExpired || isGrace || isReadOnly) && (
              <button
                onClick={handleRenew}
                disabled={processing}
                className="flex items-center gap-2 px-5 py-3 rounded-lg bg-[#10B981] text-white text-[14px] font-black hover:bg-[#059669] transition-all shadow-md disabled:opacity-50"
                title={
                  isTrialing
                    ? 'Pagás ahora y tu primer mes empieza al final del trial — los días gratis no se pierden.'
                    : undefined
                }
              >
                {processing ? (
                  <RefreshCcw size={16} className="animate-spin" />
                ) : (
                  <CreditCard size={16} />
                )}
                Pagar mi plan
              </button>
            )}
          </div>

          {/* Mensaje: los días gratis no se pierden (solo cuando está en trial) */}
          {isTrialing && (
            <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 flex items-start gap-3">
              <Shield size={18} className="text-emerald-600 shrink-0 mt-0.5" />
              <div className="text-[12px] leading-relaxed text-emerald-800">
                <strong>Te quedan {daysLeft} día{daysLeft !== 1 ? 's' : ''} gratis.</strong>{' '}
                Si activás el plan ahora, tu primer mes empieza al final del trial — los días gratis no se pierden, se suman.
              </div>
            </div>
          )}
        </div>

        {/* Warning */}
        {(isGrace || isExpired) && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex items-start gap-3">
            <AlertTriangle size={18} className="text-amber-500 shrink-0 mt-0.5" />
            <div>
              <p className="text-[13px] font-bold text-amber-800">
                {isExpired
                  ? 'Tu suscripción venció. Estás en modo solo lectura.'
                  : `Tu período de gracia termina en ${graceDaysLeft} día${graceDaysLeft === 1 ? '' : 's'}.`}
              </p>
              <p className="text-[12px] text-amber-700 mt-1">
                {isExpired
                  ? 'Podés ver toda tu información pero no podés vender, facturar ni importar datos. Elegí un plan para reactivar.'
                  : 'Después de ese plazo, el sistema entrará en modo solo lectura. Elegí tu plan para seguir operando.'}
              </p>
            </div>
          </div>
        )}

        {/* Plans grid */}
        <div className="mb-8">
          <h2 className="text-[11px] font-black uppercase tracking-widest text-[#A1A1AA] mb-4">
            Planes disponibles
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {plans.map((plan, index) => {
              const badge = getPlanBadge(plan, index);
              const isCurrent = plan.key === currentPlanKey;
              const isEnterprise = index === 2;
              const features = Array.isArray(plan.features) ? plan.features : [];

              return (
                <div
                  key={plan.key}
                  className={`relative rounded-xl border p-6 transition-all cursor-pointer ${
                    selectedPlan?.key === plan.key
                      ? 'border-[#10B981] bg-[#D1FAE5]/20 shadow-lg ring-2 ring-[#10B981]/20'
                      : isCurrent
                      ? 'border-[color:var(--color-accent)] bg-[var(--color-accent-soft)]/30 shadow-sm'
                      : isEnterprise
                      ? 'border-[#D1FAE5] bg-gradient-to-b from-[#D1FAE5]/30 to-white shadow-sm'
                      : 'border-[#E4E4E7] bg-[var(--surface)] shadow-sm hover:shadow-md hover:border-[#10B981]/50'
                  }`}
                  onClick={() => handleSelectPlan(plan)}
                >
                  {badge && (
                    <div
                      className={`absolute -top-2.5 left-4 inline-flex items-center gap-1.5 px-3 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider text-white ${badge.color}`}
                    >
                      {isCurrent ? <CheckCircle2 size={10} /> : index === 2 ? <Crown size={10} /> : <Star size={10} />}
                      {badge.label}
                    </div>
                  )}

                  <div className="mb-4 mt-1">
                    <div className="flex items-center gap-2 mb-2">
                      {planIcons[plan.key] || <Zap size={18} />}
                      <h3 className="text-[15px] font-black text-[#3F3F46]">{plan.name}</h3>
                    </div>
                    <p className="text-[12px] text-[#71717A] leading-relaxed">{plan.description}</p>
                  </div>

                  <div className="flex items-end gap-1 mb-4">
                    <span className="text-[28px] font-black text-[#3F3F46] leading-none">
                      {formatPrice(plan.price_monthly)}
                    </span>
                    <span className="text-[11px] font-bold text-[#A1A1AA] pb-1">/mes + IVA</span>
                  </div>

                  <ul className="space-y-2 mb-5">
                    {features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-[12px] text-[#3F3F46] font-medium">
                        <CheckCircle2 size={14} className="text-[#10B981] shrink-0 mt-0.5" />
                        {f}
                      </li>
                    ))}
                  </ul>

                  <div
                    className={`w-full py-2.5 rounded-lg text-center text-[11px] font-black uppercase tracking-widest transition-all ${
                      selectedPlan?.key === plan.key
                        ? 'bg-[#10B981] text-white'
                        : isCurrent
                        ? 'bg-[var(--color-accent-soft)] text-[var(--color-accent-dark)] border border-[color:var(--color-accent)]'
                        : 'bg-[#F4F4F5] text-[#71717A] border border-[#E4E4E7]'
                    }`}
                  >
                    {selectedPlan?.key === plan.key
                      ? '✓ Seleccionado'
                      : isCurrent
                      ? 'Plan actual'
                      : 'Elegir este plan'}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Confirm selection */}
        {selectedPlan && selectedPlan.key !== currentPlanKey && (
          <div className="bg-[var(--surface)] rounded-xl border-2 border-[#10B981] p-5 mb-8 shadow-lg">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="text-[10px] font-black uppercase tracking-widest text-[#10B981] mb-1">
                  Cambiar a
                </p>
                <p className="text-[18px] font-black text-[#3F3F46]">
                  {selectedPlan.name} — {formatPrice(selectedPlan.price_monthly)}/mes
                </p>
                <p className="text-[12px] text-[#A1A1AA] mt-1">
                  {selectedPlan.price_monthly > (currentPlan?.price_monthly || 0)
                    ? 'Upgrade: vas a acceder a más módulos y funcionalidades.'
                    : 'Downgrade: algunos módulos podrían desactivarse.'}
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setSelectedPlan(null)}
                  className="px-4 py-2.5 rounded-lg bg-[#F4F4F5] text-[#71717A] text-[11px] font-bold uppercase tracking-widest hover:bg-[#E4E4E7] transition-all"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleConfirmPlan}
                  disabled={processing}
                  className="flex items-center gap-2 px-6 py-2.5 rounded-lg bg-[#10B981] text-white text-[11px] font-black uppercase tracking-widest hover:bg-[#059669] transition-all shadow-md disabled:opacity-50"
                >
                  {processing ? (
                    <RefreshCcw size={14} className="animate-spin" />
                  ) : (
                    <ArrowRight size={14} />
                  )}
                  Confirmar cambio
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Payment history */}
        <div className="bg-[var(--surface)] rounded-xl border border-[#E4E4E7] shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-[#F4F4F5]">
            <h3 className="text-[11px] font-black uppercase tracking-widest text-[#A1A1AA] flex items-center gap-2">
              <Calendar size={14} /> Historial de pagos
            </h3>
          </div>

          {history.length === 0 ? (
            <div className="py-12 text-center">
              <Clock size={32} className="text-[#E4E4E7] mx-auto mb-3" />
              <p className="text-[12px] font-bold text-[#A1A1AA]">No hay pagos registrados todavía.</p>
            </div>
          ) : (
            <div className="divide-y divide-[#F4F4F5]">
              {history.map((sub) => (
                <div key={sub.id} className="px-5 py-3.5 flex items-center justify-between hover:bg-[#FAFAFA] transition-colors">
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-2 h-2 rounded-full ${
                        sub.status === 'paid'
                          ? 'bg-[var(--color-accent-dark)]'
                          : sub.status === 'pending'
                          ? 'bg-amber-400'
                          : sub.status === 'cancelled'
                          ? 'bg-red-400'
                          : 'bg-gray-300'
                      }`}
                    />
                    <div>
                      <p className="text-[12px] font-bold text-[#3F3F46]">
                        {sub.plan_name || sub.plan_key}
                      </p>
                      <p className="text-[10px] text-[#A1A1AA]">
                        {sub.period_start} → {sub.period_end}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <span className="text-[13px] font-black text-[#3F3F46]">
                      {formatPrice(sub.amount)}
                    </span>
                    <span
                      className={`px-2 py-0.5 rounded text-[9px] font-black uppercase ${
                        sub.status === 'paid'
                          ? 'bg-[var(--color-accent-soft)] text-[var(--color-accent-dark)]'
                          : sub.status === 'pending'
                          ? 'bg-amber-50 text-amber-700'
                          : sub.status === 'cancelled'
                          ? 'bg-red-50 text-red-700'
                          : 'bg-[var(--surface-2)] text-[var(--text-muted)]'
                      }`}
                    >
                      {sub.status === 'paid'
                        ? 'Pagado'
                        : sub.status === 'pending'
                        ? 'Pendiente'
                        : sub.status === 'cancelled'
                        ? 'Cancelado'
                        : sub.status}
                    </span>

                    {sub.payment_url && sub.status === 'pending' && (
                      <a
                        href={sub.payment_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-[10px] font-bold text-[#10B981] hover:underline"
                      >
                        Pagar <ExternalLink size={10} />
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Cancelar suscripción (self-service) */}
        {autoRenew && (isActive || isTrialing) && (
          <div className="mt-8 border-t border-[#E4E4E7] pt-6 text-center">
            <button
              onClick={() => setShowCancelModal(true)}
              className="text-[11px] font-bold text-[#A1A1AA] hover:text-red-600 transition-colors underline"
            >
              Cancelar mi suscripción
            </button>
          </div>
        )}

        {/* Bottom info */}
        <div className="mt-6 text-center">
          <p className="text-[10px] text-[#A1A1AA]">
            ¿Necesitás ayuda? Escribinos a{' '}
            <a
              href="https://wa.me/5493412739146"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#10B981] font-bold hover:underline"
            >
              WhatsApp
            </a>
          </p>
        </div>

        {/* Modal de cancelación */}
        {showCancelModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="w-full max-w-md rounded-2xl bg-[var(--surface)] border border-[#E4E4E7] p-6 shadow-2xl">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center">
                  <XCircle size={20} className="text-red-500" />
                </div>
                <div>
                  <h3 className="text-[16px] font-black text-[#3F3F46]">Cancelar suscripción</h3>
                  <p className="text-[12px] text-[#71717A] mt-0.5">
                    Vas a poder usar Gastify hasta el {subscriptionExpiresAt
                      ? subscriptionExpiresAt.toLocaleDateString('es-AR')
                      : 'fin del período actual'}.
                  </p>
                </div>
              </div>

              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 mb-4 text-[11px] text-amber-800">
                <strong>Importante:</strong> Después de esa fecha tu cuenta pasa a modo solo lectura.
                Vas a ver toda tu información pero no podrás vender, facturar ni cargar datos hasta que reactivés.
              </div>

              <label className="block text-[11px] font-bold text-[#3F3F46] mb-1.5 uppercase tracking-wider">
                ¿Por qué cancelás? (opcional)
              </label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                rows={3}
                placeholder="Tu feedback nos ayuda a mejorar."
                className="w-full px-3 py-2 rounded-md border border-[#E4E4E7] text-[12px] outline-none focus:border-[#10B981] resize-none"
              />

              <div className="flex gap-2 mt-5">
                <button
                  onClick={() => { setShowCancelModal(false); setCancelReason(''); }}
                  disabled={cancelling}
                  className="flex-1 py-2.5 rounded-lg bg-[#F4F4F5] text-[#71717A] text-[11px] font-bold uppercase tracking-widest hover:bg-[#E4E4E7] transition-all disabled:opacity-50"
                >
                  Volver
                </button>
                <button
                  onClick={handleCancelSubscription}
                  disabled={cancelling}
                  className="flex-1 py-2.5 rounded-lg bg-red-500 text-white text-[11px] font-black uppercase tracking-widest hover:bg-red-600 transition-all shadow-md disabled:opacity-50"
                >
                  {cancelling ? 'Cancelando...' : 'Sí, cancelar'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
